import { useState, useEffect, useCallback } from 'react';
import api, { API_ORIGIN } from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { todayStr } from '../utils/date';

const typeBadge = { Board:'badge-gray', Memory:'badge-blue', Storage:'badge-purple', Chassis:'badge-gray', Power:'badge-yellow', Network:'badge-teal' };
let tempIdCounter = 0;

// 숫자 → 쉼표 포맷 (입력용)
const formatNumber = (val) => {
  const num = String(val).replace(/[^0-9]/g, '');
  if (!num) return '';
  return Number(num).toLocaleString();
};
// 쉼표 → 순수 숫자
const parseNumber = (val) => parseInt(String(val).replace(/[^0-9]/g, '')) || 0;

const DRAFT_KEY = 'soosan_parts_draft';

export default function PartsPage() {
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('원가기준표 생성');

  const [priceSets, setPriceSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState('');
  const [parts, setParts] = useState([]); // DB에서 로드된 원본
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [partTypes, setPartTypes] = useState([]);
  const [units, setUnits] = useState([]);

  // 로컬 변경 상태
  const [editedPrices, setEditedPrices] = useState({});   // {part_id: price_string}
  const [newParts, setNewParts] = useState([]);             // [{tempId, part_type, name, ...}]
  const [deletedIds, setDeletedIds] = useState([]);         // [part_id, ...]

  // Modals
  const [showRegister, setShowRegister] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvRows, setCsvRows] = useState(null);
  const [csvMemo, setCsvMemo] = useState('');
  const [csvActivate, setCsvActivate] = useState(true);
  const [csvImporting, setCsvImporting] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [saveMemo, setSaveMemo] = useState('');
  const [saveAsActive, setSaveAsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form
  const [form, setForm] = useState({ part_type:'', name:'', model:'', manufacturer:'', spec:'', spec_display:'', unit:'EA', purchase_price:'', supplier:'', is_part:true });
  const [formError, setFormError] = useState('');

  // Compare
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [compareResult, setCompareResult] = useState(null);

  // 임시저장 복원
  const [draftLoaded, setDraftLoaded] = useState(false);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.editedPrices) setEditedPrices(draft.editedPrices);
        if (draft.newParts) { setNewParts(draft.newParts); tempIdCounter = draft.newParts.length; }
        if (draft.deletedIds) setDeletedIds(draft.deletedIds);
        if (draft.selectedSet) setSelectedSet(draft.selectedSet);
      }
    } catch (e) {}
    setDraftLoaded(true);
  }, []);

  // 변경될 때마다 임시저장
  useEffect(() => {
    if (!draftLoaded) return;
    const hasAny = Object.keys(editedPrices).length > 0 || newParts.length > 0 || deletedIds.length > 0;
    if (hasAny) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ editedPrices, newParts, deletedIds, selectedSet }));
    } else {
      localStorage.removeItem(DRAFT_KEY);
    }
  }, [editedPrices, newParts, deletedIds, selectedSet, draftLoaded]);

  useEffect(() => { loadPriceSets(); loadCodes(); }, []);

  const loadPriceSets = async () => {
    const data = await api.get('/pricesets');
    setPriceSets(data);
    const active = data.find(s => s.is_active);
    if (active && !selectedSet) setSelectedSet(String(active.id));
  };

  const loadCodes = async () => {
    const [pt, un] = await Promise.all([api.get('/settings/codes/part_type'), api.get('/settings/codes/unit')]);
    setPartTypes(pt); setUnits(un);
  };

  const loadParts = useCallback(async () => {
    if (!selectedSet) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ price_set_id: selectedSet });
      if (typeFilter) params.set('type', typeFilter);
      if (search) params.set('search', search);
      setParts(await api.get(`/parts?${params}`));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedSet, typeFilter, search]);

  useEffect(() => { loadParts(); }, [loadParts]);

  const currentSet = priceSets.find(s => String(s.id) === selectedSet);
  const isActive = currentSet?.is_active;

  // 표시할 부품 목록: 원본에서 삭제된 것 빼고 + 신규 추가
  const displayParts = [
    ...parts.filter(p => !deletedIds.includes(p.id)),
    ...newParts,
  ];

  // 변경 사항 유무
  const priceChanges = Object.keys(editedPrices).filter(id => {
    const part = parts.find(p => String(p.id) === id);
    if (!part) return false;
    return (parseInt(part.purchase_price) || 0) !== parseNumber(editedPrices[id]);
  });
  const hasChanges = priceChanges.length > 0 || newParts.length > 0 || deletedIds.length > 0;
  const changesSummary = [];
  if (newParts.length > 0) changesSummary.push(`등록 ${newParts.length}건`);
  if (deletedIds.length > 0) changesSummary.push(`삭제 ${deletedIds.length}건`);
  if (priceChanges.length > 0) changesSummary.push(`매입가 변경 ${priceChanges.length}건`);

  // 매입가 표시 (포맷된 값)
  const getPrice = (p) => {
    if (p.tempId) return formatNumber(p.purchase_price || 0);
    if (editedPrices.hasOwnProperty(String(p.id))) return formatNumber(editedPrices[String(p.id)]);
    return formatNumber(p.purchase_price || 0);
  };
  const getPriceDiff = (p) => {
    if (p.tempId) return 0;
    if (!editedPrices.hasOwnProperty(String(p.id))) return 0;
    return parseNumber(editedPrices[String(p.id)]) - (parseInt(p.purchase_price) || 0);
  };

  // 부품 등록 (로컬에 추가만)
  const resetForm = () => setForm({ part_type:'', name:'', model:'', manufacturer:'', spec:'', spec_display:'', unit:'EA', purchase_price:'', supplier:'', is_part:true });
  const handleRegister = () => {
    setFormError('');
    if (!form.name) { setFormError('자재명은 필수입니다.'); return; }
    setNewParts([...newParts, { ...form, tempId: `new-${++tempIdCounter}`, purchase_price: form.purchase_price || '0' }]);
    setShowRegister(false); resetForm();
  };

  // 부품 삭제 (로컬에서 마킹만)
  const handleDelete = (p) => {
    if (p.tempId) {
      setNewParts(newParts.filter(np => np.tempId !== p.tempId));
    } else {
      setDeletedIds([...deletedIds, p.id]);
    }
  };
  const undoDelete = (id) => setDeletedIds(deletedIds.filter(d => d !== id));

  // 부품 편집 (기본정보 - 바로 DB 저장, 매입가와 무관)
  const openEdit = (part) => {
    if (part.tempId) return; // 신규는 편집 불가 (삭제 후 재등록)
    setForm({ part_type: part.part_type||'', name: part.name, model: part.model||'', manufacturer: part.manufacturer||'', spec: part.spec||'', spec_display: part.spec_display||'', unit: part.unit||'EA', purchase_price:'', supplier: part.supplier||'', is_part: part.is_part !== false });
    setShowEdit(part); setFormError('');
  };
  const handleEdit = async () => {
    setFormError('');
    if (!form.name) { setFormError('자재명은 필수입니다.'); return; }
    try {
      await api.put(`/parts/${showEdit.id}`, { part_type:form.part_type, name:form.name, model:form.model, manufacturer:form.manufacturer, spec:form.spec, spec_display:form.spec_display, unit:form.unit, supplier:form.supplier, is_part:form.is_part });
      setShowEdit(null); resetForm(); loadParts();
    } catch (e) { setFormError(e.message); }
  };

  // 전체 취소
  const discardAll = () => { setEditedPrices({}); setNewParts([]); setDeletedIds([]); localStorage.removeItem(DRAFT_KEY); };

  // 저장 (새 원가기준표)
  const handleSave = async () => {
    if (!saveMemo) { alert('메모를 입력하세요.'); return; }
    setSaving(true);
    try {
      const priceUpdates = priceChanges.map(id => ({ part_id: parseInt(id), purchase_price: parseNumber(editedPrices[id]) }));
      const newPartsData = newParts.map(np => ({
        tempId: np.tempId, part_type: np.part_type, name: np.name, manufacturer: np.manufacturer,
        spec: np.spec, unit: np.unit, supplier: np.supplier, purchase_price: parseInt(np.purchase_price) || 0,
      }));

      const newSet = await api.post('/pricesets/save-all', {
        memo: saveMemo, activate: saveAsActive,
        new_parts: newPartsData, deleted_part_ids: deletedIds, price_updates: priceUpdates,
      });

      setShowSaveModal(false);
      localStorage.removeItem(DRAFT_KEY);
      setEditedPrices({}); setNewParts([]); setDeletedIds([]);
      await loadPriceSets();
      setSelectedSet(String(newSet.id));
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  // ===== CSV 내보내기 =====
  const exportCsv = () => {
    if (!selectedSet) return;
    const token = localStorage.getItem('token');
    fetch(`${API_ORIGIN}/api/pricesets/${selectedSet}/export-csv`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `원가기준표_${currentSet?.version || 'export'}_${todayStr()}.csv`;
      a.click(); URL.revokeObjectURL(url);
    }).catch(e => alert(e.message));
  };

  // ===== CSV 가져오기 =====
  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { alert('데이터가 없습니다.'); return; }
      const headers = lines[0].replace(/^\uFEFF/, '').split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const headerMap = {};
      headers.forEach((h, i) => { headerMap[h] = i; });

      const rows = lines.slice(1).map(line => {
        const vals = []; let current = '', inQuote = false;
        for (const ch of line) {
          if (ch === '"') { inQuote = !inQuote; }
          else if (ch === ',' && !inQuote) { vals.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        vals.push(current.trim());
        const get = (name) => vals[headerMap[name]] || '';
        return {
          code: get('자재코드'), part_type: get('자재종류'), name: get('자재명'),
          model: get('모델명'), manufacturer: get('제조사'),
          spec: get('규격'), spec_display: get('규격(견적서명시)'),
          unit: get('단위') || 'EA',
          is_part: get('부품여부') || 'Y', supplier: get('매입처'), purchase_price: get('매입가').replace(/[^0-9]/g, '') || '0',
          _isExisting: false, _oldPrice: null, _priceDiff: 0,
        };
      });
      // 기존 부품 매칭 + 빈 코드에 예상 코드 자동 부여
      const existingCodes = parts.map(p => p.code);
      const allCodes = [...existingCodes];
      // 가장 큰 코드 번호 찾기
      let maxCodeNum = 100;
      allCodes.forEach(c => { const n = parseInt(c.replace('P-', '')); if (n > maxCodeNum) maxCodeNum = n; });

      rows.forEach(r => {
        if (r.code) {
          const existing = parts.find(p => p.code === r.code);
          if (existing) {
            r._isExisting = true;
            r._old = existing;
            r._oldPrice = existing.purchase_price;
            r._priceDiff = (parseInt(r.purchase_price) || 0) - (parseInt(existing.purchase_price) || 0);
            // 필드별 변경 감지
            r._changes = [];
            if (r.part_type && r.part_type !== (existing.part_type||'')) r._changes.push('자재종류');
            if (r.name && r.name !== (existing.name||'')) r._changes.push('자재명');
            if (r.model && r.model !== (existing.model||'')) r._changes.push('모델명');
            if (r.manufacturer && r.manufacturer !== (existing.manufacturer||'')) r._changes.push('제조사');
            if (r.spec && r.spec !== (existing.spec||'')) r._changes.push('규격');
            if (r.spec_display && r.spec_display !== (existing.spec_display||'')) r._changes.push('규격(견적서명시)');
            if (r.unit && r.unit !== (existing.unit||'EA')) r._changes.push('단위');
            if (r.supplier && r.supplier !== (existing.supplier||'')) r._changes.push('매입처');
            const csvIsPart = r.is_part !== 'N';
            if (csvIsPart !== (existing.is_part !== false)) r._changes.push('부품여부');
          }
        } else {
          // 코드가 비어있으면 자동 부여
          maxCodeNum++;
          r.code = `P-${maxCodeNum}`;
          r._autoCode = true;
          allCodes.push(r.code);
        }
      });
      setCsvRows(rows);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleCsvImport = async () => {
    if (!csvMemo) { alert('메모를 입력하세요.'); return; }
    setCsvImporting(true);
    try {
      const result = await api.post('/pricesets/import-csv', { memo: csvMemo, activate: csvActivate, rows: csvRows });
      alert(`가져오기 완료: 신규 ${result.summary.created}건, 업데이트 ${result.summary.updated}건, 오류 ${result.summary.errors}건`);
      setShowCsvImport(false); setCsvRows(null); setCsvMemo('');
      await loadPriceSets();
      setSelectedSet(String(result.id));
    } catch (e) { alert(e.message); }
    finally { setCsvImporting(false); }
  };

  // Active 전환 / 삭제
  const handleActivate = async (id) => {
    if (!window.confirm('Active로 전환하시겠습니까?')) return;
    try { await api.put(`/pricesets/${id}/activate`); await loadPriceSets(); loadParts(); } catch (e) { alert(e.message); }
  };
  const handleDeletePriceSet = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try { await api.del(`/pricesets/${id}`); await loadPriceSets(); } catch (e) { alert(e.message); }
  };

  // Compare
  const runCompare = async () => {
    if (!compareA || !compareB) return;
    try { setCompareResult(await api.get(`/pricesets/compare/${compareA}/${compareB}`)); } catch (e) { alert(e.message); }
  };

  const formField = (key, label, placeholder, opts = {}) => (
    <div className="form-group" style={{ flex: opts.flex || 1 }}>
      <label className="form-label">{label}</label>
      {opts.type === 'select' ? (
        <select className="form-input" value={form[key]} onChange={e => setForm({...form, [key]:e.target.value})}>
          <option value="">선택</option>{opts.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input className="form-input" value={form[key]} onChange={e => setForm({...form, [key]:e.target.value})} placeholder={placeholder} style={opts.style} />
      )}
    </div>
  );

  return (
    <div>
      {/* Toolbar */}
      <div className="toolbar">
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontWeight:700, fontSize:13 }}>원가기준표:</span>
          <select className="form-input" style={{ width:240, padding:'7px 10px', fontSize:12 }} value={selectedSet} onChange={e => { discardAll(); setSelectedSet(e.target.value); }}>
            {priceSets.map(s => <option key={s.id} value={s.id}>{s.version} ({s.created_at?.slice(0,10)}) {s.is_active ? '★ Active' : ''}</option>)}
          </select>
          {currentSet && !currentSet.is_active && (
            <>
              {canPerformAction('원가기준표 Active 전환') && <button className="btn btn-outline btn-sm" onClick={() => handleActivate(currentSet.id)}>Active로 전환</button>}
              {canPerformAction('원가기준표 삭제') && <button className="btn btn-danger btn-sm" onClick={() => handleDeletePriceSet(currentSet.id)}>삭제</button>}
            </>
          )}
          <div className="search-box"><span>🔍</span><input placeholder="자재명, 코드 검색..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <select className="form-input" style={{ width:120, padding:'7px 10px', fontSize:12 }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">전체 종류</option>
            {partTypes.map(t => <option key={t.code} value={t.label}>{t.label}</option>)}
          </select>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline btn-sm" onClick={exportCsv}>📥 CSV 내보내기</button>
          <button className="btn btn-outline btn-sm" onClick={() => { setShowCsvImport(true); setCsvRows(null); setCsvMemo(''); setCsvActivate(true); }}>📤 CSV 가져오기</button>
          <button className="btn btn-outline btn-sm" onClick={() => { setShowCompare(true); setCompareResult(null); setCompareA(''); setCompareB(''); }}>📊 비교</button>
          {isActive && canEdit && <button className="btn btn-primary btn-sm" onClick={() => { setShowRegister(true); resetForm(); setFormError(''); }}>✚ 자재 등록</button>}
        </div>
      </div>

      {/* Set info */}
      {currentSet && (
        <div className="alert alert-info" style={{ marginBottom:16 }}>
          <strong>{currentSet.version}</strong> ({currentSet.created_at?.slice(0,10)})
          {currentSet.is_active && <span className="badge badge-blue" style={{ marginLeft:8 }}>★ Active</span>}
          <span style={{ marginLeft:12 }}>메모: {currentSet.memo}</span>
        </div>
      )}

      {/* 변경 알림 바 */}
      {hasChanges && (
        <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:10, padding:'12px 18px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:13, color:'#92400e' }}>
            <strong>⚠ 변경사항: {changesSummary.join(', ')}</strong>
            <span style={{ marginLeft:8 }}>저장하면 새 원가기준표 버전으로 생성됩니다.</span>
            <span className="badge badge-gray" style={{ marginLeft:8 }}>임시저장됨</span>
          </div>
          <div className="btn-group">
            <button className="btn btn-outline btn-sm" onClick={discardAll}>전체 취소</button>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowSaveModal(true); setSaveMemo(''); setSaveAsActive(true); }}>💾 새 버전으로 저장</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center' }}>로딩 중...</div> : (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead><tr><th>자재코드</th><th>자재종류</th><th>자재명</th><th>제조사</th><th>규격(상세)</th><th>규격(견적서명시)</th><th>단위</th><th style={{textAlign:'center'}}>부품</th><th style={{ textAlign:'right' }}>매입가</th><th style={{ textAlign:'right', width:80 }}>변동</th><th>매입처</th><th>작업</th></tr></thead>
              <tbody>
                {/* 삭제 예정 표시 */}
                {deletedIds.length > 0 && parts.filter(p => deletedIds.includes(p.id)).map(p => (
                  <tr key={`del-${p.id}`} style={{ background:'#fef2f2', textDecoration:'line-through', color:'#94a3b8' }}>
                    <td>{p.code}</td><td>{p.part_type}</td><td>{p.name}</td><td>{p.manufacturer}</td><td>{p.spec}</td><td>{p.spec_display||'-'}</td><td>{p.unit}</td>
                    <td style={{textAlign:'center'}}>{p.is_part!==false?'Y':'N'}</td>
                    <td style={{ textAlign:'right' }}>₩{Number(p.purchase_price||0).toLocaleString()}</td><td style={{ textAlign:'right', color:'#ef4444', fontWeight:700 }}>삭제</td><td>{p.supplier}</td>
                    <td><button className="btn btn-outline btn-sm" style={{ textDecoration:'none' }} onClick={() => undoDelete(p.id)}>되돌리기</button></td>
                  </tr>
                ))}
                {/* 기존 부품 (삭제 안 된 것) */}
                {parts.filter(p => !deletedIds.includes(p.id)).map(p => {
                  const diff = getPriceDiff(p);
                  const isChanged = diff !== 0;
                  return (
                    <tr key={p.id} style={isChanged ? { background:'#fffdf7' } : {}}>
                      <td style={{ fontFamily:'monospace' }}>{p.code}</td>
                      <td><span className={`badge ${typeBadge[p.part_type]||'badge-gray'}`}>{p.part_type}</span></td>
                      <td className="fw-bold">{p.name}</td><td>{p.manufacturer}</td><td>{p.spec}</td><td>{p.spec_display||'-'}</td><td>{p.unit}</td>
                      <td style={{textAlign:'center'}}><span className={`badge ${p.is_part!==false?'badge-blue':'badge-gray'}`}>{p.is_part!==false?'Y':'N'}</span></td>
                      <td style={{ textAlign:'right' }}>
                        {canEdit && isActive ? (
                          <input style={{ width:110, padding:'4px 8px', border: isChanged?'2px solid #f59e0b':'1px solid #d1d5db', borderRadius:6, fontSize:13, textAlign:'right', outline:'none', background:isChanged?'#fffbeb':'#fff' }}
                            value={getPrice(p)} onChange={e => setEditedPrices({...editedPrices, [String(p.id)]: String(parseNumber(e.target.value))})} />
                        ) : (
                          <span style={{ fontWeight:700 }}>₩{Number(p.purchase_price||0).toLocaleString()}</span>
                        )}
                      </td>
                      <td style={{ textAlign:'right', fontSize:12, fontWeight:isChanged?700:400, color:diff>0?'#ef4444':diff<0?'#22c55e':'#94a3b8' }}>
                        {diff>0?`+₩${diff.toLocaleString()}`:diff<0?`₩${diff.toLocaleString()}`:'-'}
                      </td>
                      <td>{p.supplier}</td>
                      <td><div className="btn-group">
                        {canEdit && <button className="btn btn-outline btn-sm" onClick={() => openEdit(p)}>편집</button>}
                        {canEdit && isActive && <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>삭제</button>}
                      </div></td>
                    </tr>
                  );
                })}
                {/* 신규 부품 */}
                {newParts.map(np => (
                  <tr key={np.tempId} style={{ background:'#f0fdf4' }}>
                    <td style={{ color:'#22c55e', fontWeight:700 }}>NEW</td>
                    <td><span className={`badge ${typeBadge[np.part_type]||'badge-gray'}`}>{np.part_type}</span></td>
                    <td className="fw-bold">{np.name}</td><td>{np.manufacturer}</td><td>{np.spec}</td><td>{np.spec_display||'-'}</td><td>{np.unit}</td>
                    <td style={{textAlign:'center'}}><span className={`badge ${np.is_part!==false?'badge-blue':'badge-gray'}`}>{np.is_part!==false?'Y':'N'}</span></td>
                    <td style={{ textAlign:'right' }}>
                      <input style={{ width:110, padding:'4px 8px', border:'2px solid #22c55e', borderRadius:6, fontSize:13, textAlign:'right', outline:'none', background:'#f0fdf4' }}
                        value={formatNumber(np.purchase_price)} onChange={e => setNewParts(newParts.map(x => x.tempId === np.tempId ? {...x, purchase_price: String(parseNumber(e.target.value))} : x))} />
                    </td>
                    <td style={{ textAlign:'right', color:'#22c55e', fontWeight:700 }}>신규</td>
                    <td>{np.supplier}</td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => handleDelete(np)}>취소</button></td>
                  </tr>
                ))}
                {displayParts.length === 0 && <tr><td colSpan={12} style={{ textAlign:'center', padding:32, color:'#94a3b8' }}>등록된 자재가 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="text-muted" style={{ marginTop:8 }}>* 자재 등록/삭제/매입가 수정 후 [새 버전으로 저장]을 눌러야 반영됩니다. 저장하지 않으면 모든 변경은 무효됩니다.</div>

      {/* ===== SAVE MODAL ===== */}
      {showSaveModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:520, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>새 원가기준표로 저장</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowSaveModal(false)}>✕</span>
            </div>
            <div style={{ fontSize:13, marginBottom:16, color:'#475569' }}>
              변경사항: <strong>{changesSummary.join(', ')}</strong>
            </div>
            {/* 변경 요약 */}
            <div style={{ background:'#f8fafc', borderRadius:8, padding:12, marginBottom:16, maxHeight:220, overflowY:'auto', fontSize:12 }}>
              {newParts.length > 0 && (<><div style={{ fontWeight:700, color:'#22c55e', marginBottom:4 }}>신규 등록</div>
                {newParts.map(np => <div key={np.tempId}>+ {np.name} ({np.spec}) — ₩{Number(np.purchase_price||0).toLocaleString()}</div>)}<div style={{ marginBottom:8 }} /></>)}
              {deletedIds.length > 0 && (<><div style={{ fontWeight:700, color:'#ef4444', marginBottom:4 }}>삭제</div>
                {deletedIds.map(id => { const p = parts.find(x=>x.id===id); return p ? <div key={id}>- {p.name} ({p.spec})</div> : null; })}<div style={{ marginBottom:8 }} /></>)}
              {priceChanges.length > 0 && (<><div style={{ fontWeight:700, color:'#f59e0b', marginBottom:4 }}>매입가 변경</div>
                {priceChanges.map(id => { const p = parts.find(x=>String(x.id)===id); if(!p) return null; const o=parseInt(p.purchase_price)||0; const n=parseNumber(editedPrices[id]);
                  return <div key={id}>{p.name} ({p.spec}): ₩{o.toLocaleString()} → ₩{n.toLocaleString()} ({n-o>0?'+':''}₩{(n-o).toLocaleString()})</div>; })}</>)}
            </div>
            <div className="form-group"><label className="form-label">메모 (필수)</label><input className="form-input" value={saveMemo} onChange={e => setSaveMemo(e.target.value)} placeholder="예: 원자재 인상 반영, 신규 자재 추가" /></div>
            <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:16, cursor:'pointer' }}>
              <input type="checkbox" checked={saveAsActive && canPerformAction('원가기준표 Active 전환')} disabled={!canPerformAction('원가기준표 Active 전환')} onChange={e => setSaveAsActive(e.target.checked)} /> 저장 후 Active로 전환 {!canPerformAction('원가기준표 Active 전환') && <span style={{color:'#94a3b8',fontSize:10}}>(권한 없음)</span>}
            </label>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowSaveModal(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving||!saveMemo}>{saving?'저장 중...':'저장'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== REGISTER MODAL ===== */}
      {showRegister && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:520 }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>자재 등록</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowRegister(false)}>✕</span>
            </div>
            <div className="alert alert-info" style={{ marginBottom:12 }}>ℹ 등록한 자재는 [새 버전으로 저장] 시 반영됩니다.</div>
            {formError && <div className="alert alert-danger" style={{ marginBottom:12 }}>{formError}</div>}
            <div className="form-row">{formField('part_type','자재종류','',{type:'select',options:partTypes.map(t=>t.label)})}{formField('name','자재명 *','예: CPU Board')}</div>
            <div className="form-row">{formField('model','모델명','예: NMC-0121')}{formField('manufacturer','제조사','예: Intel')}</div>
            <div className="form-row">{formField('spec','규격(상세)','예: Xeon Gold')}{formField('spec_display','규격(견적서명시)','예: 1G Copper 4Port')}</div>
            <div className="form-row">{formField('supplier','매입처','예: A전자')}</div>
            <div className="form-row">
              {formField('unit','단위','',{type:'select',options:units.map(u=>u.label)})}
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">매입가</label>
                <input className="form-input" style={{ textAlign:'right' }} value={formatNumber(form.purchase_price)} onChange={e => setForm({...form, purchase_price: String(parseNumber(e.target.value))})} placeholder="0" />
              </div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:8,marginBottom:8}}>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#64748b',cursor:'pointer'}}>부품여부 <div className={`toggle ${form.is_part?'on':''}`} onClick={()=>setForm({...form,is_part:!form.is_part})}><div className="toggle-knob"></div></div></label>
              <span style={{fontSize:11,color:'#94a3b8'}}>{form.is_part ? '부품으로 분류' : '부품 아님'}</span>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
              <button className="btn btn-outline" onClick={() => setShowRegister(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleRegister}>추가</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EDIT MODAL ===== */}
      {showEdit && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:520 }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>자재 편집 — {showEdit.code}</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowEdit(null)}>✕</span>
            </div>
            {formError && <div className="alert alert-danger" style={{ marginBottom:12 }}>{formError}</div>}
            <div className="form-row">{formField('part_type','자재종류','',{type:'select',options:partTypes.map(t=>t.label)})}{formField('name','자재명 *','')}</div>
            <div className="form-row">{formField('model','모델명','')}{formField('manufacturer','제조사','')}</div>
            <div className="form-row">{formField('spec','규격(상세)','')}{formField('spec_display','규격(견적서명시)','')}</div>
            <div className="form-row">{formField('supplier','매입처','')}</div>
            <div className="form-row">{formField('unit','단위','',{type:'select',options:units.map(u=>u.label)})}</div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4,marginBottom:8}}>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#64748b',cursor:'pointer'}}>부품여부 <div className={`toggle ${form.is_part?'on':''}`} onClick={()=>setForm({...form,is_part:!form.is_part})}><div className="toggle-knob"></div></div></label>
              <span style={{fontSize:11,color:'#94a3b8'}}>{form.is_part ? '부품으로 분류' : '부품 아님'}</span>
            </div>
            <div className="alert alert-info" style={{ marginBottom:0 }}>ℹ 매입가는 테이블에서 직접 수정하세요. 기본정보 변경은 즉시 저장됩니다.</div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
              <button className="btn btn-outline" onClick={() => setShowEdit(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleEdit}>저장</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CSV IMPORT MODAL ===== */}
      {showCsvImport && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:750, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>CSV로 원가기준표 가져오기</span>
              <span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowCsvImport(false)}>✕</span>
            </div>

            {!csvRows ? (
              <>
                <div style={{ border:'2px dashed #d1d5db', borderRadius:10, padding:32, textAlign:'center', cursor:'pointer', color:'#94a3b8', position:'relative', marginBottom:16 }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📄</div>
                  CSV 파일을 선택하여 업로드
                  <input type="file" accept=".csv" onChange={handleCsvFile} style={{ position:'absolute', inset:0, opacity:0, cursor:'pointer' }} />
                </div>
                <div className="alert alert-info">
                  ℹ CSV 형식: 자재코드, 자재종류, 자재명, 모델명, 제조사, 규격, 규격(견적서명시), 단위, 부품여부(Y/N), 매입처, 매입가<br/>
                  현재 원가기준표를 [CSV 내보내기]한 파일을 수정하여 업로드하면 편리합니다.
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom:12, fontSize:13 }}>
                  전체 <strong>{csvRows.length}</strong>건:
                  <span style={{ color:'#22c55e', fontWeight:700, marginLeft:8 }}>신규 {csvRows.filter(r=>!r._isExisting).length}</span>
                  <span style={{ color:'#3b82f6', fontWeight:700, marginLeft:8 }}>변경없음 {csvRows.filter(r=>r._isExisting && r._priceDiff===0 && (!r._changes||r._changes.length===0)).length}</span>
                  <span style={{ color:'#f59e0b', fontWeight:700, marginLeft:8 }}>변경 {csvRows.filter(r=>r._isExisting && (r._priceDiff!==0 || (r._changes&&r._changes.length>0))).length}</span>
                </div>
                <div style={{ maxHeight:400, overflowY:'auto', marginBottom:16 }}>
                  <table style={{ fontSize:11 }}>
                    <thead><tr><th>상태</th><th>자재코드</th><th>자재종류</th><th>자재명</th><th>모델명</th><th>제조사</th><th>규격</th><th>단위</th><th>부품</th><th>매입처</th><th style={{textAlign:'right'}}>매입가</th><th>변경사항</th></tr></thead>
                    <tbody>
                      {csvRows.map((r, i) => {
                        const hasChanges = r._isExisting && (r._priceDiff!==0 || (r._changes&&r._changes.length>0));
                        const ch = (field) => r._changes && r._changes.includes(field) ? {background:'#fef3c7',fontWeight:700} : {};
                        return (
                          <tr key={i} style={{ background: !r._isExisting ? '#f0fdf4' : hasChanges ? '#fffbeb' : 'transparent' }}>
                            <td>
                              {!r._isExisting ? <span style={{ color:'#22c55e', fontWeight:700 }}>신규</span>
                                : hasChanges ? <span style={{ color:'#f59e0b', fontWeight:700 }}>변경</span>
                                : <span style={{ color:'#94a3b8' }}>동일</span>}
                            </td>
                            <td style={{ fontFamily:'monospace' }}>{r.code}{r._autoCode && <span style={{color:'#22c55e', fontSize:10, marginLeft:4}}>(자동)</span>}</td>
                            <td style={ch('자재종류')}>{r.part_type}</td>
                            <td className="fw-bold" style={ch('자재명')}>{r.name}</td>
                            <td style={ch('모델명')}>{r.model||'-'}</td>
                            <td style={ch('제조사')}>{r.manufacturer||'-'}</td>
                            <td style={ch('규격')}>{r.spec||'-'}</td>
                            <td style={ch('단위')}>{r.unit}</td>
                            <td style={{textAlign:'center',...ch('부품여부')}}>{r.is_part==='N'?'N':'Y'}</td>
                            <td style={ch('매입처')}>{r.supplier||'-'}</td>
                            <td style={{ textAlign:'right', fontWeight:r._priceDiff?700:400, color:r._priceDiff>0?'#ef4444':r._priceDiff<0?'#22c55e':'inherit' }}>
                              ₩{Number(r.purchase_price||0).toLocaleString()}
                              {r._isExisting && r._priceDiff!==0 && <div style={{fontSize:10}}>{r._priceDiff>0?'+':''}₩{r._priceDiff.toLocaleString()}</div>}
                            </td>
                            <td style={{fontSize:10,color:'#f59e0b',maxWidth:120}}>
                              {!r._isExisting ? '전체 신규' : hasChanges ? [...(r._changes||[]), ...(r._priceDiff!==0?['매입가']:[])].join(', ') : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="form-group">
                  <label className="form-label">메모 (필수)</label>
                  <input className="form-input" value={csvMemo} onChange={e => setCsvMemo(e.target.value)} placeholder="예: CSV 가져오기 - 2026년 상반기 단가표" />
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:16, cursor:'pointer' }}>
                  <input type="checkbox" checked={csvActivate} onChange={e => setCsvActivate(e.target.checked)} /> 가져오기 후 Active로 전환
                </label>
                <div style={{ display:'flex', gap:8, justifyContent:'space-between' }}>
                  <button className="btn btn-outline" onClick={() => setCsvRows(null)}>다시 선택</button>
                  <div className="btn-group">
                    <button className="btn btn-outline" onClick={() => setShowCsvImport(false)}>취소</button>
                    <button className="btn btn-primary" onClick={handleCsvImport} disabled={csvImporting || !csvMemo}>{csvImporting ? '가져오는 중...' : '가져오기'}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== COMPARE MODAL ===== */}
      {showCompare && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:700, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>원가기준표 비교</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowCompare(false)}>✕</span>
            </div>
            <div className="form-row" style={{ marginBottom:16 }}>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">비교 A</label>
                <select className="form-input" value={compareA} onChange={e=>{setCompareA(e.target.value);setCompareResult(null);}}>
                  <option value="">선택</option>{priceSets.map(s=><option key={s.id} value={s.id}>{s.version} ({s.created_at?.slice(0,10)}){s.is_active?' ★':''}</option>)}</select></div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">비교 B</label>
                <select className="form-input" value={compareB} onChange={e=>{setCompareB(e.target.value);setCompareResult(null);}}>
                  <option value="">선택</option>{priceSets.map(s=><option key={s.id} value={s.id}>{s.version} ({s.created_at?.slice(0,10)}){s.is_active?' ★':''}</option>)}</select></div>
              <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:14 }}><button className="btn btn-primary btn-sm" onClick={runCompare} disabled={!compareA||!compareB}>비교</button></div>
            </div>
            {compareResult && (
              <table style={{ fontSize:12 }}>
                <thead><tr><th>자재코드</th><th>자재명</th><th>규격</th><th style={{textAlign:'right'}}>{compareResult.set_a}</th><th style={{textAlign:'right'}}>{compareResult.set_b}</th><th style={{textAlign:'right'}}>변동</th></tr></thead>
                <tbody>{compareResult.items.map((item,i)=>{const diff=parseInt(item.diff)||0;const isNew=!item.price_a&&item.price_b;const isRemoved=item.price_a&&!item.price_b;
                  return(<tr key={i} style={isNew?{background:'#eff6ff'}:isRemoved?{background:'#fef2f2'}:diff!==0?{background:'#fffbeb'}:{}}>
                    <td style={{fontFamily:'monospace'}}>{item.code}</td><td>{item.name}</td><td>{item.spec}</td>
                    <td style={{textAlign:'right'}}>{item.price_a?`₩${Number(item.price_a).toLocaleString()}`:'-'}</td>
                    <td style={{textAlign:'right'}}>{item.price_b?`₩${Number(item.price_b).toLocaleString()}`:'-'}</td>
                    <td style={{textAlign:'right',fontWeight:700,color:isNew?'#3b82f6':isRemoved?'#ef4444':diff>0?'#ef4444':diff<0?'#22c55e':'#94a3b8'}}>
                      {isNew?'신규':isRemoved?'삭제':diff>0?`+₩${diff.toLocaleString()}`:diff<0?`₩${diff.toLocaleString()}`:'-'}</td></tr>);})}</tbody>
              </table>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}><button className="btn btn-outline" onClick={()=>setShowCompare(false)}>닫기</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
